"use client";

import { useState, useSyncExternalStore } from "react";

import { safeSessionRemove, safeSessionSet } from "@/lib/browser-storage";
import { getSEOIntentConfig, normalizeSEOIntent, type SEOIntentConfig } from "@/lib/seo-intents";
import { AI_ASTROLOGER_ONBOARDING_CONTRACT } from "./onboarding-contract";
import styles from "./ConsultationHero.module.css";

/**
 * A concern is an optional refinement on the chat-intent surface. The choices
 * use stable keys; analytics never receives a seeker's free-text concern.
 */
function trackTopicSelection(properties: Record<string, unknown>) {
  void import("@/lib/analytics-events").then(({ trackEvent }) => {
    trackEvent("topic_select_changed", properties);
  });
}

const subscribeToHydration = () => () => {};
const clientHydrationSnapshot = () => true;
const serverHydrationSnapshot = () => false;

function useHydrated(): boolean {
  return useSyncExternalStore(
    subscribeToHydration,
    clientHydrationSnapshot,
    serverHydrationSnapshot
  );
}

export default function TopicSelect({
  source = "/ai-astrologer/",
  onIntentCaptured,
  onSkip,
}: {
  source?: string;
  onIntentCaptured: (entry: ConcernSelection) => void;
  onSkip: () => void;
}) {
  const isHydrated = useHydrated();
  const [question, setQuestion] = useState("");
  const trimmedQuestion = question.trim();
  const validQuestion = trimmedQuestion.length > 0 && question.length <= 500;

  function selectConcern(topic: {
    key: string;
    title: string;
    prompt: string;
    seoIntent: string;
    concern?: string;
    lens: string;
  }) {
    const intent = normalizeSEOIntent(topic.seoIntent);
    const config = getSEOIntentConfig(intent);
    const capturedConfig = { ...config, prompt: topic.prompt };
    try {
      // The downstream ChatPanel reads this value as a backend concern. Keep
      // the SEO config in the controlled entry below, and persist the native
      // concern here so the first greeting actually answers the chosen pattern.
      if (topic.concern) {
        safeSessionSet("seo_entry_intent", topic.concern);
      } else {
        safeSessionRemove("seo_entry_intent");
      }
      safeSessionSet("seo_entry_prompt", topic.prompt);
      safeSessionSet("seo_entry_topic", topic.key);
      safeSessionSet("seo_entry_path", source);
      safeSessionSet("seo_entry_page_category", config.pageCategory);
      safeSessionSet("seo_entry_primary_offer", config.primaryOffer);
      safeSessionSet("seo_entry_report_plan_id", config.reportPlanId);
      safeSessionSet("seo_entry_auto_send", "1");
    } catch {
      // sessionStorage may be disabled (private browsing); event still fires.
    }
    onIntentCaptured({
      config: capturedConfig,
      sourcePath: source,
      topicKey: topic.key,
      topicTitle: topic.title,
      topicLens: topic.lens,
    });
    trackTopicSelection({
      intent: config.intent,
      topic_key: topic.key,
      // Stable, non-PII journey dimensions. The customer-facing title/prompt
      // remain in the chat handoff and are never duplicated into analytics.
      source_path: source,
      source_page: source,
    });
  }

  function skipConcern() {
    clearConcernSelection();
    onSkip();
    trackTopicSelection({
      topic_key: "skip",
      source_path: source,
      source_page: source,
    });
  }

  return (
    <fieldset
      className="m-0 border-0 p-0"
      data-hydrated={isHydrated ? "true" : "false"}
      data-testid="ai-astrologer-topic-select"
    >
      <legend className={styles.topicLegend}>
        {AI_ASTROLOGER_ONBOARDING_CONTRACT.concerns.legend}
      </legend>
      <p className={styles.topicHelper}>{AI_ASTROLOGER_ONBOARDING_CONTRACT.concerns.helper}</p>
      <div className={styles.topicMeta}>
        <details className={styles.customQuestion}>
          <summary className="min-h-11 content-center cursor-pointer py-1 text-body-small font-semibold text-ish-dark">
            Write your own question
          </summary>
          <label htmlFor="ai-astrologer-question" className="block text-body-small text-ish-dark">
            Your question
          </label>
          <textarea
            id="ai-astrologer-question"
            className="mt-2 w-full rounded-lg border border-ish-border bg-ish-bg-card p-3 text-body-small text-ish-dark"
            rows={2}
            maxLength={500}
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            aria-describedby="ai-astrologer-question-help"
            disabled={!isHydrated}
          />
          <p id="ai-astrologer-question-help" className={styles.topicHelper}>
            Your first answer uses your birth details below. Up to 500 characters.
          </p>
          <button
            type="button"
            className={`${styles.skipButton} mt-2`}
            disabled={!isHydrated || !validQuestion}
            onClick={() => {
              if (!validQuestion) return;
              selectConcern({
                key: "custom_question",
                title: trimmedQuestion,
                prompt: trimmedQuestion,
                seoIntent: "chart_report",
                lens: "Your question · first answer free",
              });
            }}
          >
            Use this question
          </button>
        </details>
        <button
          type="button"
          disabled={!isHydrated}
          onClick={skipConcern}
          className={styles.skipButton}
        >
          {AI_ASTROLOGER_ONBOARDING_CONTRACT.concerns.skipLabel}
        </button>
      </div>
      <div className={styles.topicGrid}>
        {AI_ASTROLOGER_ONBOARDING_CONTRACT.concerns.options.map((topic) => (
          <button
            key={topic.key}
            type="button"
            disabled={!isHydrated}
            onClick={() => selectConcern(topic)}
            className={styles.topicButton}
          >
            <span className={styles.topicTitle}>{topic.title}</span>
            <span className={styles.topicLens}>{topic.lens}</span>
          </button>
        ))}
      </div>
    </fieldset>
  );
}

export type ConcernSelection = {
  config: SEOIntentConfig;
  sourcePath: string;
  topicKey: string;
  topicTitle: string;
  topicLens: string;
};

export function clearConcernSelection() {
  for (const key of [
    "seo_entry_intent",
    "seo_entry_prompt",
    "seo_entry_topic",
    "seo_entry_path",
    "seo_entry_page_category",
    "seo_entry_primary_offer",
    "seo_entry_report_plan_id",
    "seo_entry_auto_send",
  ]) {
    safeSessionRemove(key);
  }
}
